import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import {
  Figure,
  LoadMore,
  NameLink,
  PersonScopeCard,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
  partyHref,
  useMoney,
} from '@/pages/cvm/shared';
import { cvmSettlementParams, fetchCvmSettlements } from '@/services/cvm';
import type { CvmSettlementRow, CvmYearShares } from '@/services/types';

const PAGE_SIZE = 50;
const OUTCOMES = ['ACCEPTED', 'REJECTED'] as const;

/**
 * Termos de compromisso: the Colegiado's decision on each proposal.
 *
 * One row per proponent, accepted or rejected, with the amount the decision
 * states. A settlement is not a sanction and is not pooled with one: the
 * amount is a payment the proponent offered, and this screen's ladder is over
 * accepted amounts only. Where the source links a judged case the row opens
 * it; most proposals close the process before any judgment.
 */
export function CvmSettlementsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [outcome, setOutcome] = useState(seed.get('outcome') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const filters = {
    q,
    outcome: outcome || null,
    year: year ? Number(year) : null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'settlements', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmSettlements({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:settlements.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:settlements.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:settlements.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <>
          <section className="rounded-lg border bg-card p-4" data-shape>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:settlements.shape.title')}
            </h2>
            <Answer answer={data.shape} help={t('cvm:settlements.shape.help')}>
              {(s) => (
                <>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Figure label={t('cvm:settlements.shape.rows')} value={number.format(s.rows)} />
                    <Figure
                      label={t('cvm:settlements.shape.accepted')}
                      value={number.format(s.accepted)}
                    />
                    <Figure
                      label={t('cvm:settlements.shape.rejected')}
                      value={number.format(s.rejected)}
                    />
                    <Figure
                      label={t('cvm:settlements.shape.withAmount')}
                      value={number.format(s.with_amount)}
                    />
                  </dl>
                  <dl className="mt-2 grid grid-cols-3 gap-2">
                    <Figure
                      label={t('cvm:settlements.shape.withUnanimity')}
                      value={number.format(s.with_unanimity)}
                    />
                    <Figure
                      label={t('cvm:settlements.shape.linked')}
                      value={number.format(s.linked_to_judgment)}
                    />
                    <Figure
                      label={t('cvm:settlements.shape.decisions')}
                      value={number.format(s.decisions)}
                    />
                  </dl>
                </>
              )}
            </Answer>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <FineDistributionPanel
              answer={data.amounts}
              titleKey="cvm:settlements.amounts.title"
              helpKey="cvm:settlements.amounts.help"
              countKey="cvm:settlements.amounts.count"
            />
            <section className="rounded-lg border bg-card p-4" data-by-year>
              <h2 className="mb-2 text-sm font-semibold text-foreground">
                {t('cvm:settlements.byYear.title')}
              </h2>
              <Answer answer={data.by_year} help={t('cvm:settlements.byYear.help')}>
                {(rows: CvmYearShares[]) => (
                  <div className="max-h-72 overflow-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead className={THEAD}>
                        <tr>
                          <th className={TH}>{t('cvm:settlements.byYear.year')}</th>
                          {OUTCOMES.map((code) => (
                            <th key={code} className={TH_NUM}>
                              {t(`cvm:settlementOutcomes.${code}`)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.year} className="border-t">
                            <td className={TD}>{row.year}</td>
                            {OUTCOMES.map((code) => {
                              const share = row.shares.find((s) => s.code === code);
                              return (
                                <td key={code} className={TD_NUM}>
                                  {share ? number.format(share.count) : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Answer>
            </section>
          </div>
        </>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-settlement-query" className="text-xs font-medium text-foreground">
            {t('cvm:settlements.search.label')}
          </label>
          <input
            id="cvm-settlement-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:settlements.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label
            htmlFor="cvm-settlement-outcome"
            className="block text-xs font-medium text-foreground"
          >
            {t('cvm:settlements.filters.outcome')}
          </label>
          <select
            id="cvm-settlement-outcome"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:settlements.filters.any')}</option>
            {OUTCOMES.map((code) => (
              <option key={code} value={code}>
                {t(`cvm:settlementOutcomes.${code}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label
            htmlFor="cvm-settlement-year"
            className="block text-xs font-medium text-foreground"
          >
            {t('cvm:settlements.filters.year')}
          </label>
          <input
            id="cvm-settlement-year"
            inputMode="numeric"
            value={year}
            onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:settlements.search.submit')}
        </button>
        <div className="ml-auto">
          <ExportButton
            path={`/api/v1/cvm/export/settlements?${cvmSettlementParams(filters).toString()}`}
          />
        </div>
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:settlements.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:settlements.columns.decided')}</th>
                  <th className={TH}>{t('cvm:settlements.columns.process')}</th>
                  <th className={TH}>{t('cvm:settlements.columns.party')}</th>
                  <th className={TH}>{t('cvm:settlements.columns.outcome')}</th>
                  <th className={TH_NUM}>{t('cvm:settlements.columns.amount')}</th>
                  <th className={TH}>{t('cvm:settlements.columns.relator')}</th>
                  <th className={TH}>{t('cvm:settlements.columns.source')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <SettlementRow key={`${row.decision_url}-${row.party_slug}-${index}`} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      {data ? <PersonScopeCard answer={data.person_scope} /> : null}
      <FieldGuide i18nKey="cvm:settlements.guide" />
    </div>
  );
}

function SettlementRow({ row }: { row: CvmSettlementRow }) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD_MUTED}>
        <span className="tabular-nums text-foreground">{row.decided_at ?? '—'}</span>
        {row.signed_at ? <div>{t('cvm:settlements.signed', { date: row.signed_at })}</div> : null}
      </td>
      <td className={TD}>
        {row.case_key ? (
          <Link
            to={caseHref(row.case_key)}
            className="text-primary underline-offset-2 hover:underline"
          >
            {row.process_number ?? row.process_key}
          </Link>
        ) : (
          <span className="text-muted-foreground">
            {row.process_number ?? row.process_key ?? '—'}
          </span>
        )}
        {row.reg_number ? (
          <div className="text-xs text-muted-foreground">{row.reg_number}</div>
        ) : null}
      </td>
      <td className={TD}>
        <NameLink name={row.party} href={partyHref(row.party_slug)} />
        {row.entity_type ? (
          <div className="text-xs text-muted-foreground">
            {t(`cvm:entityTypes.${row.entity_type}`, {
              defaultValue: row.entity_type,
            })}
          </div>
        ) : null}
      </td>
      <td className={TD_MUTED}>
        <span className="text-foreground">
          {t(`cvm:settlementOutcomes.${row.outcome}`, {
            defaultValue: row.outcome,
          })}
        </span>
        {row.is_unanimous == null ? null : (
          <div>
            {row.is_unanimous
              ? t('cvm:case.settlements.unanimous')
              : t('cvm:case.settlements.majority')}
          </div>
        )}
      </td>
      <td className={TD_NUM}>
        {money(row.amount_brl)}
        {row.amount_is_split ? (
          <div className="text-xs font-normal text-muted-foreground">
            {t('cvm:case.settlements.split')}
          </div>
        ) : null}
      </td>
      <td className={TD_MUTED}>{row.relator ?? '—'}</td>
      <td className={TD_MUTED}>
        <a
          href={row.decision_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {t('cvm:case.settlements.decision')}
        </a>
      </td>
    </tr>
  );
}
